import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { Dialog } from "@/components/modals";
import KindBadge from "@/components/courses/KindBadge";
import { promptPreview, questionTypeCaption } from "@/lib/questionScoring";

/**
 * Handpick review for assistant-staged changes.
 *
 * Renders each staged patch in the outline's own vocabulary (Unit / Lesson / Item
 * cards, question rows with points pills) so accepting reads like the builder,
 * not like a chat summary. Questions are selectable one by one — you can take
 * three of fourteen and drop the rest.
 */

export type StagedQuestion = {
  type: string;
  prompt: string;
  points: number;
  presentation?: string;
  options?: { id: number; label?: string; left?: string; right?: string }[];
  correct?: { optionId?: number; optionIds?: number[]; answer?: string; blanks?: string[] };
  awardAttemptPoints?: boolean;
  attemptPoints?: number;
};

export interface AssistantPatch {
  patchId: string;
  tool: string;
  kind?: string;
  label: string;
  target?: string;
  preview?: string;
  input: {
    parentId?: string;
    itemId?: string;
    questionId?: string;
    title?: string;
    type?: string;
    estimatedMinutes?: number;
    hourBucket?: string;
    coreQuestion?: string;
    objectives?: string[];
    bodyMarkdown?: string;
    terms?: { term: string; definition: string }[];
    questions?: StagedQuestion[];
    secondaryTaxonomy?: string | null;
    tertiaryTaxonomy?: string | null;
    secondaryCategoryLabel?: string;
    tertiaryCategoryLabel?: string;
    categoryProposed?: { title: string; type?: string }[];
    /** update_question / update_item: live snapshot for the review diff. */
    before?: {
      prompt?: string;
      points?: number;
      answerKey?: string;
      gaps?: string[];
      title?: string;
      estimatedMinutes?: number;
      hourBucket?: string;
      body?: string;
      terms?: string;
      extras?: string;
    };
    question?: StagedQuestion;
    [key: string]: unknown;
  };
}

const ITEM_TYPE_LABELS: Record<string, string> = {
  assessment: "Assessment",
  discussion: "Discussion",
  exam: "Exam",
  homework: "Homework",
  key_terms: "Key terms",
  lab: "Lab",
  project: "Project",
  reading: "Reading",
};

/** Same U/L/I color language as the outline rows. */
function kindMark(patch: AssistantPatch) {
  if (patch.kind === "unit") return "U";
  if (patch.kind === "lesson") return "L";
  return "I";
}

function kindWord(patch: AssistantPatch) {
  if (patch.kind === "unit") return "Unit";
  if (patch.kind === "lesson") return "Lesson";
  if (patch.kind === "question") return "Question";
  return "Item";
}

/** One-line answer key, matching what the server shows the assistant. `*` = correct. */
function answerKeyLine(question: StagedQuestion) {
  const lines = answerLines(question);
  if (!lines.length) return "(no answer)";
  return lines.map((line) => `${line.correct ? "*" : "-"} ${line.text}`).join("  |  ");
}

function patchTitle(patch: AssistantPatch) {
  return String(patch.input.title || "").trim() || patch.label;
}

function patchVerb(patch: AssistantPatch) {
  if (patch.tool === "update_item" || patch.tool === "update_question") return "Edit";
  if (patch.tool === "add_questions") return "Add";
  return "New";
}

function questionCount(patch: AssistantPatch) {
  return Array.isArray(patch.input.questions) ? patch.input.questions.length : 0;
}

function isTmpId(id?: string) {
  return Boolean(id && id.startsWith("tmp:"));
}

function parentRefs(patch: AssistantPatch) {
  return [patch.input.parentId, patch.input.itemId].filter(
    (id): id is string => isTmpId(id) && id !== patch.patchId,
  );
}

/** Answer key lines for one staged question, in the type's own vocabulary. */
function answerLines(question: StagedQuestion): { text: string; correct?: boolean }[] {
  const type = question.type;
  const options = Array.isArray(question.options) ? question.options : [];
  if (type === "matching") {
    return options.map((pair) => ({ text: `${pair.left || "—"} → ${pair.right || "—"}` }));
  }
  if (type === "single_choice" || type === "multiple_choice") {
    const ids = new Set<number>(
      Array.isArray(question.correct?.optionIds)
        ? question.correct!.optionIds!.map(Number)
        : question.correct?.optionId != null
          ? [Number(question.correct.optionId)]
          : [],
    );
    return options.map((opt) => ({ text: opt.label || "—", correct: ids.has(Number(opt.id)) }));
  }
  if (type === "fill_blank") {
    const blanks =
      Array.isArray(question.correct?.blanks) && question.correct.blanks.length
        ? question.correct.blanks
        : String(question.correct?.answer || "").trim()
          ? [String(question.correct?.answer)]
          : [];
    if (!blanks.length) return [{ text: "No answer key yet" }];
    return blanks.map((value, i) => ({
      text: blanks.length > 1 ? `Blank ${i + 1}: ${value || "—"}` : String(value || "—"),
      correct: true,
    }));
  }
  if (type === "long_answer") {
    const sample = String(question.correct?.answer || "").trim();
    return sample
      ? [{ text: `Teacher scored · sample: ${sample}` }]
      : [{ text: "Teacher scored — no answer key" }];
  }
  if (type === "directions") return [];
  const answer = String(question.correct?.answer || "").trim();
  return answer ? [{ text: answer, correct: true }] : [{ text: "No answer key yet" }];
}

function QuestionRow({
  question,
  index,
  checked,
  disabled,
  onToggle,
}: {
  question: StagedQuestion;
  index: number;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const [open, setOpen] = useState(false);
  const lines = answerLines(question);
  return (
    <li className={`al-cb-rv-q${checked ? "" : " al-cb-rv-q--off"}`}>
      <div className="al-cb-rv-q-head">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-label={`Include question ${index + 1}`}
          onChange={onToggle}
        />
        <div className="al-cb-rv-q-main">
          <p className="al-cb-qcard-prompt">{promptPreview(question.prompt)}</p>
          <p className="al-cb-qcard-meta">
            {questionTypeCaption(question.type, question.presentation).toLowerCase()}
            {question.awardAttemptPoints ? ` · ${question.attemptPoints ?? 0} attempt pts` : ""}
          </p>
        </div>
        <span className="al-cb-pts-chip al-cb-pts-chip--locked">{question.points} pts</span>
        {lines.length ? (
          <button
            type="button"
            className="al-cb-icon-btn"
            aria-label={open ? "Hide answer key" : "Show answer key"}
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            <ChevronDown size={14} className={open ? "al-cb-rv-caret--open" : undefined} />
          </button>
        ) : null}
      </div>
      {open && lines.length ? (
        <ul className="al-cb-rv-key">
          {lines.map((line, i) => (
            <li key={i} className={line.correct ? "al-cb-rv-key--on" : undefined}>
              {line.correct ? <Check size={12} aria-label="Correct" /> : null}
              <span>{line.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Before/after for a single-question fix, so the answer key change is obvious. */
function QuestionDiff({
  question,
  before,
}: {
  question: StagedQuestion;
  before?: { prompt?: string; points?: number; answerKey?: string; gaps?: string[] };
}) {
  const nextKey = answerKeyLine(question);
  const rows: { label: string; was?: string; now: string }[] = [];
  const nextPrompt = promptPreview(question.prompt);
  if (before?.prompt && before.prompt !== question.prompt) {
    rows.push({ label: "Prompt", was: promptPreview(before.prompt), now: nextPrompt });
  }
  if (before?.points != null && before.points !== question.points) {
    rows.push({ label: "Points", was: `${before.points}`, now: `${question.points}` });
  }
  if (!before?.answerKey || before.answerKey !== nextKey) {
    rows.push({ label: "Answer key", was: before?.answerKey, now: nextKey });
  }

  return (
    <div className="al-cb-rv-body">
      <div className="al-cb-rv-field">
        <span>Question</span>
        <p className="al-cb-qcard-prompt">{nextPrompt}</p>
        <p className="al-cb-qcard-meta">
          {questionTypeCaption(question.type, question.presentation).toLowerCase()} · {question.points} pts
        </p>
      </div>
      {before?.gaps?.length ? (
        <p className="al-cb-rv-field">
          <span>Fixes</span>
          {before.gaps.join(" ")}
        </p>
      ) : null}
      {rows.length ? (
        <DiffTable rows={rows} />
      ) : (
        <p className="al-cb-rv-field">No change.</p>
      )}
    </div>
  );
}

function collapseText(value: string, max = 1200) {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

function termsPlain(rows: { term: string; definition: string }[]) {
  if (!rows.length) return "(none)";
  return rows
    .map((row) => `${row.term?.trim() || "—"} — ${row.definition?.trim() || ""}`)
    .join("\n");
}

function bucketLabel(raw?: string) {
  const t = String(raw || "").trim();
  if (!t) return "(none)";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function DiffTable({ rows }: { rows: { label: string; was?: string; now: string }[] }) {
  return (
    <dl className="al-cb-rv-diff">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>
            {row.was ? <span className="al-cb-rv-was">{row.was}</span> : null}
            <span className="al-cb-rv-now">{row.now}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Before/after for an existing item rewrite (body, minutes, extras, title, terms). */
function ItemDiff({ patch }: { patch: AssistantPatch }) {
  const { input } = patch;
  const before = input.before;
  const rows: { label: string; was?: string; now: string }[] = [];

  if (input.title != null) {
    const now = String(input.title).trim() || "Untitled";
    if (!before?.title || before.title !== now) {
      rows.push({ label: "Title", was: before?.title, now });
    }
  }
  if (input.estimatedMinutes != null) {
    const now = String(input.estimatedMinutes);
    if (before?.estimatedMinutes == null || String(before.estimatedMinutes) !== now) {
      rows.push({
        label: "Minutes",
        was: before?.estimatedMinutes != null ? String(before.estimatedMinutes) : undefined,
        now,
      });
    }
  }
  if (input.hourBucket) {
    const now = bucketLabel(String(input.hourBucket));
    const was = bucketLabel(before?.hourBucket);
    if (was !== now) rows.push({ label: "Hour type", was: before?.hourBucket ? was : undefined, now });
  }
  const extrasTouched =
    input.secondaryTaxonomy !== undefined ||
    input.tertiaryTaxonomy !== undefined ||
    Boolean(input.secondaryCategoryLabel) ||
    Boolean(input.tertiaryCategoryLabel);
  if (extrasTouched) {
    const now =
      [input.secondaryCategoryLabel, input.tertiaryCategoryLabel].filter(Boolean).join(" · ") ||
      "(none)";
    if ((before?.extras || "(none)") !== now) {
      rows.push({ label: "Extras", was: before?.extras, now });
    }
  }
  if (Array.isArray(input.terms)) {
    const now = termsPlain(input.terms);
    if ((before?.terms || "(none)") !== now) {
      rows.push({ label: "Key terms", was: before?.terms, now });
    }
  }
  if (input.bodyMarkdown != null) {
    rows.push({
      label: "Body",
      was: before?.body,
      now: collapseText(input.bodyMarkdown) || "(empty)",
    });
  }

  return (
    <div className="al-cb-rv-body">
      {rows.length ? <DiffTable rows={rows} /> : <p className="al-cb-rv-field">No change.</p>}
      {input.categoryProposed?.length ? (
        <p className="al-cb-rv-field">
          <span>Not in catalog</span>
          {input.categoryProposed.map((row) => row.title).join(", ")}
        </p>
      ) : null}
    </div>
  );
}

function PatchBody({
  patch,
  dropped,
  disabled,
  onToggleQuestion,
}: {
  patch: AssistantPatch;
  dropped: Set<number>;
  disabled: boolean;
  onToggleQuestion: (index: number) => void;
}) {
  const { input } = patch;
  if (patch.tool === "update_question" && input.question) {
    return <QuestionDiff question={input.question} before={input.before} />;
  }
  if (patch.tool === "update_item") {
    return <ItemDiff patch={patch} />;
  }
  const questions = Array.isArray(input.questions) ? input.questions : [];
  const terms = Array.isArray(input.terms) ? input.terms : [];
  const objectives = Array.isArray(input.objectives) ? input.objectives : [];
  const body = String(input.bodyMarkdown || "").trim();
  const extraLabels = [input.secondaryCategoryLabel, input.tertiaryCategoryLabel].filter(Boolean);

  return (
    <div className="al-cb-rv-body">
      {input.coreQuestion ? (
        <p className="al-cb-rv-field">
          <span>Core question</span>
          {input.coreQuestion}
        </p>
      ) : null}
      {objectives.length ? (
        <div className="al-cb-rv-field">
          <span>Objectives</span>
          <ul className="al-cb-rv-list">
            {objectives.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {extraLabels.length ? (
        <p className="al-cb-rv-field">
          <span>Extra categories</span>
          {extraLabels.join(" · ")}
        </p>
      ) : null}
      {body ? (
        <div className="al-cb-rv-field">
          <span>{patch.tool === "update_item" ? "Body (replaces the current body)" : "Body"}</span>
          <div className="al-cb-rv-prose">{body}</div>
        </div>
      ) : null}
      {terms.length ? (
        <div className="al-cb-rv-field">
          <span>{terms.length} key terms</span>
          <ul className="al-cb-rv-list">
            {terms.map((row, i) => (
              <li key={i}>
                <strong>{row.term}</strong> — {row.definition}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {questions.length ? (
        <div className="al-cb-rv-field">
          <span>
            {questions.length - dropped.size} of {questions.length} questions selected
          </span>
          <ul className="al-cb-rv-qs">
            {questions.map((question, i) => (
              <QuestionRow
                key={i}
                question={question}
                index={i}
                checked={!dropped.has(i)}
                disabled={disabled}
                onToggle={() => onToggleQuestion(i)}
              />
            ))}
          </ul>
        </div>
      ) : null}
      {!input.coreQuestion &&
      !objectives.length &&
      !body &&
      !terms.length &&
      !questions.length &&
      !extraLabels.length ? (
        <p className="al-cb-rv-field">{patch.preview || "No extra detail."}</p>
      ) : null}
    </div>
  );
}

export default function AssistantReview({
  open,
  patches,
  applying,
  error,
  onClose,
  onReject,
  onApply,
}: {
  open: boolean;
  patches: AssistantPatch[];
  applying: boolean;
  error?: string;
  onClose: () => void;
  onReject: (patchId: string) => void;
  onApply: (rows: AssistantPatch[]) => void;
}) {
  const [off, setOff] = useState<Record<string, true>>({});
  const [dropped, setDropped] = useState<Record<string, number[]>>({});

  // A patch that arrives after the panel is open starts selected.
  useEffect(() => {
    const live = new Set(patches.map((row) => row.patchId));
    setOff((prev) => {
      const next: Record<string, true> = {};
      for (const id of Object.keys(prev)) if (live.has(id)) next[id] = true;
      return next;
    });
    setDropped((prev) => {
      const next: Record<string, number[]> = {};
      for (const [id, rows] of Object.entries(prev)) if (live.has(id)) next[id] = rows;
      return next;
    });
  }, [patches]);

  const droppedSet = useMemo(() => {
    const map: Record<string, Set<number>> = {};
    for (const patch of patches) map[patch.patchId] = new Set(dropped[patch.patchId] || []);
    return map;
  }, [patches, dropped]);

  const isOn = (patch: AssistantPatch) => !off[patch.patchId];

  /** Selection follows the tmp-id chain: dropping a parent drops its children. */
  const setSelected = (patchId: string, on: boolean) => {
    setOff((prev) => {
      const next = { ...prev };
      if (on) {
        delete next[patchId];
        // Turning a child on needs its staged parents on too.
        let grew = true;
        while (grew) {
          grew = false;
          for (const row of patches) {
            if (next[row.patchId]) continue;
            for (const ref of parentRefs(row)) {
              if (next[ref]) {
                delete next[ref];
                grew = true;
              }
            }
          }
        }
      } else {
        next[patchId] = true;
        let grew = true;
        while (grew) {
          grew = false;
          for (const row of patches) {
            if (next[row.patchId]) continue;
            if (parentRefs(row).some((ref) => next[ref])) {
              next[row.patchId] = true;
              grew = true;
            }
          }
        }
      }
      return next;
    });
  };

  const toggleQuestion = (patchId: string, index: number) => {
    setDropped((prev) => {
      const rows = new Set(prev[patchId] || []);
      if (rows.has(index)) rows.delete(index);
      else rows.add(index);
      return { ...prev, [patchId]: [...rows] };
    });
  };

  // add_questions with nothing selected has nothing to save, so it drops out.
  const selected = patches
    .filter(isOn)
    .map((patch) => {
      const skip = droppedSet[patch.patchId];
      const questions = Array.isArray(patch.input.questions) ? patch.input.questions : null;
      if (!questions || !skip?.size) return patch;
      return { ...patch, input: { ...patch.input, questions: questions.filter((_, i) => !skip.has(i)) } };
    })
    .filter((patch) => patch.tool !== "add_questions" || questionCount(patch) > 0);

  const totalQuestions = selected.reduce((sum, patch) => sum + questionCount(patch), 0);
  const locked = applying;

  return (
    <Dialog
      open={open}
      title="Review changes"
      subtitle="Nothing is saved until you save. Uncheck anything you don't want."
      width="44rem"
      maxHeight="min(88vh, 46rem)"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="al-cb-btn al-cb-btn--ghost" disabled={locked} onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="al-cb-btn"
            disabled={locked || selected.length === 0}
            onClick={() => onApply(selected)}
          >
            {applying ? <Loader2 size={14} className="al-cb-spin" aria-hidden="true" /> : <Check size={14} />}
            Save {selected.length} change{selected.length === 1 ? "" : "s"}
            {totalQuestions ? ` · ${totalQuestions} q` : ""}
          </button>
        </>
      }
    >
      {error ? <p className="al-cb-asst-error">{error}</p> : null}
      {patches.length === 0 ? (
        <p className="al-cb-muted">Nothing is waiting.</p>
      ) : (
        <div className="al-cb-rv">
          {patches.map((patch) => {
            const on = isOn(patch);
            const typeLabel = ITEM_TYPE_LABELS[String(patch.input.type || "")] || "";
            return (
              <section
                key={patch.patchId}
                className={`al-cb-rv-card al-cb-rv-card--${kindMark(patch)}${on ? "" : " al-cb-rv-card--off"}`}
              >
                <header className="al-cb-rv-head">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={locked}
                    aria-label={`Include ${patch.label}`}
                    onChange={() => setSelected(patch.patchId, !on)}
                  />
                  <KindBadge mark={kindMark(patch)} label={kindWord(patch)} />
                  <strong className="al-cb-rv-title">{patchTitle(patch)}</strong>
                  {typeLabel ? <span className="al-cb-badge">{typeLabel}</span> : null}
                  <span className="al-cb-badge al-cb-badge--proposed">{patchVerb(patch)}</span>
                  {patch.input.estimatedMinutes ? (
                    <span className="al-cb-pill">{patch.input.estimatedMinutes} min</span>
                  ) : null}
                  <button
                    type="button"
                    className="al-cb-icon-btn"
                    aria-label={`Discard ${patch.label}`}
                    disabled={locked}
                    onClick={() => onReject(patch.patchId)}
                  >
                    <X size={14} />
                  </button>
                </header>
                {patch.target ? (
                  <p className="al-cb-rv-target">
                    {patch.tool === "add_questions"
                      ? "Appends to"
                      : patch.tool === "update_question"
                        ? "In"
                        : "Goes in"}{" "}
                    {patch.target}
                  </p>
                ) : null}
                <PatchBody
                  patch={patch}
                  dropped={droppedSet[patch.patchId] || new Set()}
                  disabled={locked || !on}
                  onToggleQuestion={(index) => toggleQuestion(patch.patchId, index)}
                />
              </section>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
